import { ReactNode, useEffect, useRef, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { buildClasses } from "../utils/buildClasses";
import { RYE_SLIDE_EASING, RYE_SLIDE_MS } from "./motion";

/**
 * Slides inline content open and closed with Bread's standard slide (see
 * motion.ts), fading as it goes. Closing keeps the last content in place until
 * the slide finishes.
 */
export function RyeReveal({
  show,
  children,
}: {
  show: boolean;
  children: ReactNode;
}): JSX.Element {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [present, setPresent] = useState(show);
  const lastShown = useRef(children);
  if (show) {
    lastShown.current = children;
  }

  useEffect(() => {
    if (show) {
      setPresent(true);
      return;
    }
    if (reduceMotion) {
      setPresent(false);
      return;
    }
    const timeout = window.setTimeout(
      () => setPresent(false),
      RYE_SLIDE_MS + 50,
    );
    return () => window.clearTimeout(timeout);
  }, [show, reduceMotion]);

  const durationMs = reduceMotion ? 0 : RYE_SLIDE_MS;
  const transition = {
    transitionDuration: `${durationMs}ms`,
    transitionTimingFunction: RYE_SLIDE_EASING,
  };

  return (
    <div
      aria-hidden={!show}
      {...{ inert: !show ? "" : undefined }}
      className={buildClasses(
        "grid",
        "transition-[grid-template-rows]",
        show ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
      style={transition}
    >
      <div
        className={buildClasses(
          "min-h-0",
          "overflow-hidden",
          "transition-[opacity,transform]",
          show ? "opacity-100" : "opacity-0",
          show ? "translate-y-0" : "-translate-y-2",
        )}
        style={transition}
      >
        {show || present ? (show ? children : lastShown.current) : null}
      </div>
    </div>
  );
}
